import React, { useCallback, useRef, useState } from "react";
import cx from "classnames";
import styles from "./InvoiceAddress.module.scss";
import { useTranslation } from "react-i18next";
import { AddressWithSearch } from "../../../../components/address/AddressWithSearch";
import { Address, InvoiceType } from "../../../../data/models/ContractTypes";
import { useContract } from "../../../../hooks/useContract";
import lodashIsEqual from "lodash.isequal";
import { ContractEditError } from "../ContractEditError";
import { Form } from "../../../../components/form/Form";
import { hasRealErrors } from "../../../../components/form/FormHelpers";
import { FormName } from "../menus/ContractEditMenu";
import { ContractSaveError, handleError } from "../ContractPage";
import { dataContracts } from "../../../../data/dataContracts";
import { useContractId } from "../../../../hooks/useContractId";
import { ONGOING_RESPONSE } from "../../../../data/models/CommonTypes";
import { ServerError } from "../../../../network/API";
import { UNAUTHORIZED_STATUS_CODE } from "../../../../data/data";
import { useAtomValue, useSetAtom } from "jotai";
import {
  contractErrorState,
  contractSaveState,
} from "../../../../state/contractSaveState";
import { useMutation } from "@tanstack/react-query";
import { contractInvoiceState } from "../../../../state/contractInvoiceState";

interface Props {
  disabled?: boolean;
}

export const InvoiceAddress: React.FunctionComponent<Props> = ({
  disabled,
}) => {
  const invoiceType = useAtomValue(contractInvoiceState);
  const shouldValidate = invoiceType.invoiceType === InvoiceType.ADDRESS;
  const setDataError = useSetAtom(contractErrorState);
  const setDataSaved = useSetAtom(contractSaveState);
  const contract = useContract();
  const contractId = useContractId();
  const { t } = useTranslation();
  const [address, setAddress] = useState(
    contract.contractData.invoiceAddress as Address
  ) || {
    street: "",
    postalCode: "",
    city: "",
    countryCode: "",
  };

  const onClose = useCallback(() => setError(null), []);
  const [error, setError] = useState<ContractSaveError | null>(null);
  const latestSaved = useRef<Address>(address);
  const latestErroneousSaved = useRef<Address | null>(null);
  const [displayableError, setDisplayableError] = useState<
    string | undefined
  >();
  const saveTimer = useRef<number>(undefined);

  const { mutate: trySaveAddress } = useMutation({
    mutationFn: (invoiceAddress: Address) => {
      setDisplayableError(undefined);
      if (lodashIsEqual(invoiceAddress, latestSaved.current)) {
        return Promise.resolve(invoiceAddress);
      }

      return dataContracts.saveInvoiceAddress(contractId, invoiceAddress);
    },
    onSuccess: () => {
      latestSaved.current = address;
      setDataSaved((dataSaved) =>
        dataSaved.concat({
          date: new Date(),
        })
      );
    },
    onError: (err: unknown, updated: Address, context) => {
      if (err === ONGOING_RESPONSE) {
        return;
      }

      if ((err as ServerError<unknown>).status === UNAUTHORIZED_STATUS_CODE) {
        return;
      }

      const untypedError = err as any;

      if (typeof untypedError?.data === "string") {
        setDisplayableError(untypedError?.data);
      } else if (typeof untypedError?.data?.message === "string") {
        setDisplayableError(untypedError?.data?.message);
      } else {
        setDisplayableError(undefined);
      }

      latestErroneousSaved.current = updated;
      handleError(err as ServerError<unknown>, setError);
      setAddress(latestSaved.current as Address);
      setDataError((dataErrors) =>
        dataErrors.concat({
          date: new Date(),
        })
      );
    },
  });

  const saveAddress = useCallback(
    (copy: Address) => {
      clearTimeout(saveTimer.current);

      saveTimer.current = window.setTimeout(() => {
        trySaveAddress(copy);
      }, 500);
    },
    [trySaveAddress]
  );

  const retry = useCallback(() => {
    if (!latestErroneousSaved.current) {
      return;
    }

    setError(null);

    setTimeout(() => {
      if (!latestErroneousSaved.current) {
        return;
      }

      trySaveAddress(latestErroneousSaved.current as Address);
    }, 500);
  }, [trySaveAddress]);

  return (
    <div className={cx(styles.invoiceAddress)}>
      <ContractEditError
        error={error}
        displayableError={displayableError}
        retry={latestErroneousSaved.current ? retry : undefined}
        onClose={onClose}
      />

      <Form
        name={FormName.INVOICE_ADDRESS}
        onSaveTrigger={(_, form) => {
          const realErrors = hasRealErrors(form);

          if (realErrors) {
            return;
          }

          saveAddress(address);
        }}
      >
        <AddressWithSearch
          address={
            address || {
              street: "",
              postalCode: "",
              city: "",
              countryCode: "",
            }
          }
          onChange={(copy) => {
            setAddress(copy);
            saveAddress(copy);
          }}
          addressRequiredFields={{
            street: shouldValidate,
            city: shouldValidate,
            postalCode: shouldValidate,
            countryCode: shouldValidate,
          }}
          showCountryCode
          disabled={disabled}
        />

        <div
          className={cx(styles.addressCopy, {
            [styles.active]: lodashIsEqual(
              address,
              contract.contractData.address
            ),
            [styles.show]: !!contract.contractData.address,
          })}
        >
          {t("Use ")}{" "}
          <button
            disabled={
              disabled || lodashIsEqual(address, contract.contractData.address)
            }
            onClick={(ev) => {
              ev.preventDefault();

              if (!contract.contractData.address) {
                return;
              }

              setAddress(contract.contractData.address);
              saveAddress(contract.contractData.address);
            }}
          >
            {contract.contractData.address?.street},{" "}
            {contract.contractData.address?.postalCode}{" "}
            {contract.contractData.address?.city}
          </button>
        </div>
      </Form>
    </div>
  );
};
